import React, { useMemo, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SortableTable, { ReportToolbar } from "@/components/reports/SortableTable";
import { num, pct, inRange, exportCsv, exportPdf, captureCharts } from "@/components/reports/reportUtils";

const medal = ["bg-amber-100 text-amber-800 border-amber-200", "bg-slate-100 text-slate-700 border-slate-200", "bg-orange-100 text-orange-800 border-orange-200"];

const ChartCard = ({ title, children }) => (
  <Card className="p-5 border-slate-200 shadow-none mt-6" data-chart-capture data-chart-title={title}>
    <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
    {children}
  </Card>
);

export default function OperatorEfficiency({ production, team, jobs }) {
  const rootRef = useRef(null);
  const [f, setF] = useState({ from: "", to: "", job: "All", sort: "efficiency" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const teamMap = useMemo(() => Object.fromEntries(team.map((t) => [t.id, t])), [team]);

  const rows = useMemo(() => {
    const filtered = production
      .filter((p) => inRange(p.date, f.from, f.to))
      .filter((p) => f.job === "All" || p.job_id === f.job);
    const map = {};
    filtered.forEach((p) => {
      const key = p.operator_id || "unknown";
      const t = teamMap[key];
      const r = map[key] || (map[key] = {
        id: key, code: t?.code || "—", name: t?.name || "Unassigned", role: t?.role || "—",
        planned: 0, actual: 0, output: 0, jobs: new Set(), logs: 0,
      });
      r.planned += num(p.planned_hours);
      r.actual += num(p.actual_hours);
      r.output += num(p.actual_output);
      r.logs += 1;
      if (p.job_id) r.jobs.add(p.job_id);
    });
    const list = Object.values(map).map((r) => {
      const variance = r.actual - r.planned;
      const efficiency = r.actual > 0 ? (r.planned / r.actual) * 100 : 0;
      return {
        ...r, jobCount: r.jobs.size, variance, efficiency,
        outputPerHour: r.actual > 0 ? r.output / r.actual : 0,
        __rowClass: efficiency >= 100 ? "bg-emerald-50" : efficiency < 75 && r.actual > 0 ? "bg-red-50" : "",
      };
    }).filter((r) => r.planned > 0 || r.actual > 0);

    const key = f.sort === "hours" ? "actual" : f.sort === "output" ? "output" : "efficiency";
    return list.sort((a, b) => b[key] - a[key]).map((r, i) => ({ ...r, rank: i + 1 }));
  }, [production, teamMap, f]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    const totPlanned = rows.reduce((s, r) => s + r.planned, 0);
    const totActual = rows.reduce((s, r) => s + r.actual, 0);
    const byEff = [...rows].filter((r) => r.actual > 0).sort((a, b) => b.efficiency - a.efficiency);
    return {
      best: byEff[0], worst: byEff[byEff.length - 1],
      overall: totActual ? (totPlanned / totActual) * 100 : 0,
      totPlanned, totActual,
    };
  }, [rows]);

  const columns = [
    { key: "rank", label: "#", align: "center", sortValue: (r) => r.rank, render: (r) => <Badge variant="outline" className={r.rank <= 3 ? medal[r.rank - 1] : "bg-white text-slate-500"}>{r.rank}</Badge>, pdf: (r) => r.rank, csv: (r) => r.rank },
    { key: "code", label: "Emp ID", render: (r) => <span className="font-mono font-medium">{r.code}</span> },
    { key: "name", label: "Operator" },
    { key: "role", label: "Role" },
    { key: "jobCount", label: "Jobs", align: "right", value: (r) => r.jobCount },
    { key: "planned", label: "Planned Hrs", align: "right", sortValue: (r) => r.planned, value: (r) => r.planned.toFixed(1) },
    { key: "actual", label: "Actual Hrs", align: "right", sortValue: (r) => r.actual, value: (r) => r.actual.toFixed(1) },
    { key: "variance", label: "Variance Hrs", align: "right", sortValue: (r) => r.variance, render: (r) => <span className={r.variance > 0 ? "text-red-600 font-medium" : "text-emerald-600 font-medium"}>{(r.variance > 0 ? "+" : "") + r.variance.toFixed(1)}</span>, pdf: (r) => (r.variance > 0 ? "+" : "") + r.variance.toFixed(1), csv: (r) => r.variance.toFixed(1) },
    { key: "efficiency", label: "Efficiency %", align: "right", sortValue: (r) => r.efficiency, render: (r) => <Badge variant="outline" className={r.efficiency >= 100 ? "bg-emerald-100 text-emerald-700 border-emerald-200" : r.efficiency >= 75 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-red-100 text-red-700 border-red-200"}>{r.efficiency > 999 ? "999%+" : pct(r.efficiency)}</Badge>, pdf: (r) => (r.efficiency > 999 ? "999%+" : pct(r.efficiency)), csv: (r) => r.efficiency.toFixed(1) },
    { key: "output", label: "Output", align: "right", sortValue: (r) => r.output, value: (r) => r.output },
    { key: "outputPerHour", label: "Output / Hr", align: "right", sortValue: (r) => r.outputPerHour, value: (r) => r.outputPerHour.toFixed(2), csv: (r) => r.outputPerHour.toFixed(2) },
  ];

  const hoursData = rows.slice(0, 15).map((r) => ({ name: r.name, Planned: Number(r.planned.toFixed(1)), Actual: Number(r.actual.toFixed(1)) }));
  const effData = rows.slice(0, 15).map((r) => ({ name: r.name, efficiency: Number(Math.min(r.efficiency, 999).toFixed(1)) }));

  return (
    <div ref={rootRef} data-testid="operator-efficiency-report">
      <ReportToolbar
        title="Operator Efficiency Ranking"
        subtitle="Planned vs actual hours across jobs · 100% = on plan, below 100% = took longer than planned"
        onCsv={() => exportCsv("operator_efficiency", columns, rows)}
        onPdf={async () => exportPdf({
          title: "Operator Efficiency Ranking",
          subtitle: `${rows.length} operators${f.from || f.to ? ` · ${f.from || "start"} to ${f.to || "today"}` : ""}`,
          columns, rows, filename: "operator_efficiency",
          charts: await captureCharts(rootRef.current),
        })}
      >
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-400">From</Label>
          <Input type="date" value={f.from} onChange={(e) => set("from", e.target.value)} className="h-9 w-36" data-testid="oe-filter-from" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-400">To</Label>
          <Input type="date" value={f.to} onChange={(e) => set("to", e.target.value)} className="h-9 w-36" data-testid="oe-filter-to" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-400">Job</Label>
          <Select value={f.job} onValueChange={(v) => set("job", v)}>
            <SelectTrigger className="h-9 w-40" data-testid="oe-filter-job"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="All">All</SelectItem>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.code}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-400">Rank by</Label>
          <Select value={f.sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger className="h-9 w-44" data-testid="oe-filter-rankby"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="efficiency">Efficiency %</SelectItem>
              <SelectItem value="hours">Actual hours</SelectItem>
              <SelectItem value="output">Output</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </ReportToolbar>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            ["Most Efficient", stats.best?.name || "—", `Efficiency ${pct(stats.best?.efficiency)}`, "text-emerald-600"],
            ["Needs Support", stats.worst?.name || "—", `Efficiency ${pct(stats.worst?.efficiency)}`, "text-red-600"],
            ["Shop Efficiency", pct(stats.overall), `${stats.totPlanned.toFixed(1)} planned / ${stats.totActual.toFixed(1)} actual hrs`, "text-slate-900"],
            ["Operators Ranked", String(rows.length), "with logged hours", "text-slate-900"],
          ].map(([label, value, sub, tone]) => (
            <Card key={label} className="p-4 border-slate-200 shadow-none">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
              <div className={`text-lg font-bold mt-1 ${tone}`} data-testid={`oe-stat-${label.toLowerCase().replace(/ /g, "-")}`}>{value}</div>
              <div className="text-xs text-slate-400 mt-0.5 truncate">{sub}</div>
            </Card>
          ))}
        </div>
      )}

      <SortableTable columns={columns} rows={rows} initialSort={{ key: "rank", dir: "asc" }} testId="oe" />

      <ChartCard title="Planned vs Actual Hours by Operator">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={hoursData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={0} angle={-20} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Planned" fill="#2563eb" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Actual" fill="#16a34a" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Efficiency % Ranking">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={effData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={0} angle={-20} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip formatter={(v) => `${v}%`} />
            <Bar dataKey="efficiency" name="Efficiency %" radius={[3, 3, 0, 0]}>
              {effData.map((d, i) => <Cell key={i} fill={d.efficiency >= 100 ? "#16a34a" : d.efficiency >= 75 ? "#eab308" : "#dc2626"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
